import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import AuthContext from "../../../../auth/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CourseService from "../../../../services/CourseService";
import AlertService from "../../../../services/AlertService";
import QuizService from "../../../../services/QuizService";
import SessionsIcon from "./../../../../assets/icons/Sessions.svg";
import WhiteSessionIcon from "./../../../../assets/icons/WhiteSessionIcon.svg";
import AttendeesIcon from "../../../../assets/icons/Attendees.svg";
import WhiteAttendeesIcon from "../../../../assets/icons/WhiteAttendeesIcon.svg";
import EditMCQIcon from "../../../../assets/icons/edit.svg";
import WhiteEditMCQIcon from "../../../../assets/icons/WhiteEditIcon.svg";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { FaTrash } from "react-icons/fa";
import { ThemeContext } from "../../../../theme/ThemeContext";

const Quiz = () => {
  const navigate = useNavigate();
  const { quizName } = useParams();
  const { state } = useLocation();
  const storedId = sessionStorage.getItem("quizId");
  const id = state?.id || storedId;
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [permissions, setPermissions] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const submitControllerRef = useRef(null);

  const times = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1) * 5,
    label: `${(i + 1) * 5} mins`,
  }));

  // States initialization
  const [quizId, setQuizId] = useState(0);
  const [qName, setQName] = useState("");
  const [courseId, setCourseId] = useState("");
  const [mcq, setMCQ] = useState(false);
  const [quizTimeInMinutes, setQuizTimeInMinutes] = useState("");
  const authContext = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  // Permissions
  useEffect(() => {
    const quizAccess = authContext.getPermissions("quizmaster");
    if (quizAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: quizAccess.some((f) => f.access.includes(2)),
      showEdit: quizAccess.some((f) => f.access.includes(3)),
      showDelete: quizAccess.some((f) => f.access.includes(4)),
    });
  }, [authContext, navigate]);

  // Load Courses
const loadCourses = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetCourses(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort(); 
  }, [loadCourses]);

const loadQuiz = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await QuizService.Get(id, authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const d = data.data;
        setQName(d.quizName || "");
        setCourseId(d.courseId?.toString() || "");
        setMCQ(d.mcq);
        setQuizTimeInMinutes(d.quizTimeInMinutes?.toString() || "");
        setScheduledDate(
          d.scheduledDate
            ? new Date(d.scheduledDate).toISOString().split("T")[0]
            : ""
        );
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, id]);

useEffect(() => {
    const controller = new AbortController(); 
    if (id) {
      setIsEditing(true);
      setQuizId(id);
      loadQuiz(controller.signal);
    }
    return () => controller.abort(); 
  }, [loadQuiz, id]);

  // Validation
  const validate = () => {
    const newErrors = {};

    if (!qName.trim()) {
      newErrors.qName = "Quiz Name is required";
    } else if (qName.trim().length < 3) {
      newErrors.qName = "Quiz Name must be at least 3 characters";
    }
    if (!courseId) {
      newErrors.courseId = "Please select a course";
    }
    if (!mcq) {
      newErrors.quizType = "Quiz Type is required";
    }

    if (!quizTimeInMinutes) {
      newErrors.duration = "Duration is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit


const onSubmitHandler = async (event) => {
  event.preventDefault();

  if (!validate()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      quizName: qName.trim(),
      courseId: Number(courseId),
      mcq,
      quizTimeInMinutes: quizTimeInMinutes ? Number(quizTimeInMinutes) : 0,
      scheduledDate: scheduledDate || null,
    };

    if (quizId > 0) payload.id = quizId;

    const data = await QuizService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Quiz saved successfully!");
      navigate("/masters-quizzes");
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.error(ex);
    AlertService.error("Something went wrong!");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const onDeleteHandler = async () => {
    const data = await QuizService.Remove(quizId, authContext);
    if (data.isSuccess) {
      AlertService.success("Quiz deleted successfully!");
      navigate("/masters-quizzes");
      sessionStorage.removeItem("quizId");
    } else {
      AlertService.error(data.errorMessage);
    }
  };

  const onUserHandler = () =>
    navigate(`/masters-quizzes/${quizName}/quiz-mapping`, { state: { id: id } });
  const onSessionsHandler = () =>
    navigate(`/masters-quizzes/${quizName}/sessions`, { state: { id: id } });
  const onAttendeesHandler = () =>
    navigate(`/masters-quizzes/${quizName}/attendees`, { state: { id: id } });
  const onEditHandler = () =>
    navigate(`/masters-quizzes/${quizName}/mcq`, { state: { id: id } });

  return (
    <div className="container-fluid custom-container mt-3">
      {/* Action Buttons */}
      {permissions.showEdit && quizId > 0 && (
        <div className="d-flex flex-wrap justify-content-end gap-3 gap-md-5 mb-3">
            <button
            className="d-flex align-items-center text-primary card-btn-link"
            onClick={onEditHandler}
          >
            <img
              src={isDark ? WhiteEditMCQIcon : EditMCQIcon}
              alt="Edit MCQ"
              className="me-2"
            />
            Edit MCQ
          </button>
          <button
            className="d-flex align-items-center text-primary card-btn-link"
            onClick={onUserHandler}
          >
            <img
               src={isDark ? WhiteAttendeesIcon : AttendeesIcon}
              alt="Sessions"
              className="me-2"
            />
            User Map
          </button>
          <button
            className="d-flex align-items-center text-primary card-btn-link"
            onClick={onSessionsHandler}
          >
            <img
              src={isDark ? WhiteSessionIcon : SessionsIcon}
              alt="Sessions"
              className="me-2"
            />
            Sessions
          </button>
          <button
            className="d-flex align-items-center text-primary card-btn-link"
            onClick={onAttendeesHandler}
          >
            <img
              src={isDark ? WhiteAttendeesIcon : AttendeesIcon}
              alt="Attendees"
              className="me-2"
            />
            Attendees
          </button>
        </div>
      )}

      <Form className="ms-2 mt-4" onSubmit={onSubmitHandler} noValidate>
        <Form.Group as={Row} className="mb-4 pb-2">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Quiz Name <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Col md={10}>
            <Form.Control
              type="text"
              value={qName}
              onChange={(e) => {
                setQName(e.target.value);
                setErrors({ ...errors, qName: undefined });
              }}
              disabled={loading}
              isInvalid={!!errors.qName}
              className="custom-input custom-input-color"
            />
            <Form.Control.Feedback type="invalid">
              {errors.qName}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-4 pb-2">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Course Name <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Col md={10}>
            <Form.Select
              value={courseId}
              onChange={(e) => {
                const value = e.target.value;
                setCourseId(value);
                setErrors({ ...errors, courseId: undefined });
              }}
              disabled={loading}
              isInvalid={!!errors.courseId}
              className="custom-input custom-input-color"
            >
              <option value="" disabled>
                Select Course
              </option>
              {courses.map((c, i) => (
                <option key={i} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.courseId}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>
     
        <Form.Group as={Row} className="mb-4">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Scheduled Date
          </Form.Label>
          <Col md={4}>
            <Form.Control
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              onKeyDown={(e) => e.preventDefault()}
onPaste={(e) => e.preventDefault()}
onFocus={(e) => {
  if (e.target.showPicker) {
    e.target.showPicker();
  }
}}
              disabled={loading}
              className="custom-input custom-input-color"
            />
          </Col>
        </Form.Group>

        {/* Quiz Type */}
        <Form.Group as={Row} className="mb-4">
          <Form.Label column md={2} className="fw-light pt-0">
            Quiz Type <span className="text-danger ms-1">*</span>
          </Form.Label>

          <Col md={10}>
            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                className="me-4"
                label="MCQ"
                checked={mcq}
                onChange={(e) => {
                  setMCQ(e.target.checked);
                  setErrors({ ...errors, quizType: undefined });
                }}
              />
            </div>
            {errors.quizType && (
              <div className="text-danger  small">{errors.quizType}</div>
            )}
          </Col>
        </Form.Group>

        {/* Duration */}
        <Form.Group as={Row} className="mb-4">
          <Form.Label
            column
            md={2}
            className="d-flex align-items-center fw-light"
          >
            Duration<span className="text-danger ms-1">*</span>
          </Form.Label>
          <Col md={10}>
            <Form.Select
              value={quizTimeInMinutes}
              onChange={(e) => {
                setQuizTimeInMinutes(e.target.value);
                setErrors({ ...errors, duration: undefined });
              }}
              disabled={loading}
              isInvalid={!!errors.duration}
              className="custom-input custom-input-color"
            >
              <option value="" disabled>
                Select Duration
              </option>
              {times.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Form.Select>

            <Form.Control.Feedback type="invalid">
              {errors.duration}
            </Form.Control.Feedback>
          </Col>
        </Form.Group>

        {/* Buttons */}
        <Row className="mt-5 mb-3">
          <Col md={{ span: 10, offset: 2 }}>
            <div className="d-flex gap-3">
              {permissions.showDelete && quizId !== 0 && (
                <Button
                  variant="outline-danger"
                  className="px-lg-5 py-2"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                >
                  <FaTrash className="me-2 mb-1" />
                  Delete
                </Button>
              )}
              <div className="ms-auto d-flex gap-4">
                <Button
                  variant="outline-secondary"
                  className="px-lg-5 py-2"
                  onClick={() => navigate("/masters-quizzes")}
                >
                  Cancel
                </Button>
                {((permissions.showAdd && quizId === 0) ||
                  (permissions.showEdit && quizId !== 0)) && (
                  <Button
                    variant="primary"
                    className="px-lg-5 py-2"
                    type="submit"
                    disabled={loading}
                  >
                    {isEditing ? "Update" : "Submit"}
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Delete Confirm */}
        <ConfirmDialogBox
          visible={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          onConfirm={onDeleteHandler}
          title="Are you sure you want to delete this quiz?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      </Form>
    </div>
  );
};

export default Quiz;
